import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  setActiveChat,
  setActiveQuestion,
  setShowWelcomeText,
  setIsArchived,
} from "../../redux/actions/chatActions";
import { fetchChatHistory } from "../../redux/reducers/chatHistorySlice";
import { AppDispatch, RootState } from "../../redux/store";

import { Button } from "pepsico-ds";

import { sessionStorageKeys } from "../../config/config";
import { useAppSelector } from "../../hooks/app";
import {
  ChatHistoryTab,
  setActiveTab,
} from "../../redux/reducers/chatHistoryActionReducer";
import ChatQuestionHistory from "./ChatQuestionHistory/ChatQuestionHistory";
import FavouriteList from "./FavouriteHistroy/FavouriteList/FavouriteList";
import ArchieveList from "./ArchiveHistory/ArchiveList/ArchieveList";

const PAGE_SIZE = 15;

const ChatHistory: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { activeTab, title, tabs } = useAppSelector(
    (state) => state.chatHistoryAction
  );
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>();
  const { loading, hasLoaded } = useSelector(
    (state: RootState) => state.chatHistory
  );
  const [isFavouriteLoading, setIsFavouriteLoading] = useState(false);
  const [favouriteError, setFavouriteError] = useState<string | null>(null);

  const { activeChatId } = useSelector((state: RootState) => state.chat);

  const [page, setPage] = useState(1);
  const [favouritePage, setFavouritePage] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isFetchingMoreFavourites, setIsFetchingMoreFavourites] =
    useState(false);
  const [isNewChat, setIsNewChat] = useState(false);

  const chatListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(setActiveTab("chatHistory"));
  }, [dispatch]);

  useEffect(() => {
    if (!hasLoaded) {
      dispatch(fetchChatHistory({ page: 1, limit: PAGE_SIZE }));
      fetchFavorites(1);
    }
  }, [dispatch, hasLoaded, urlSessionId]);

  const fetchFavorites = async (pageNum: number) => {
    try {
      setIsFavouriteLoading(true);
      // await dispatch(
      //   fetchFavouriteSessions({ page: pageNum, limit: PAGE_SIZE })
      // );
      setFavouritePage(pageNum);
      setFavouriteError(null);
    } catch (err) {
      setFavouriteError("Failed to load favorite chats.");
    } finally {
      setIsFavouriteLoading(false);
      setIsFetchingMoreFavourites(false);
    }
  };

  useEffect(() => {
    if (urlSessionId && urlSessionId !== activeChatId) {
      dispatch(setActiveChat(urlSessionId));
    }
  }, [dispatch, urlSessionId, activeChatId]);

  const loadMoreChats = () => {
    if (isFetchingMore || loading) return;

    setIsFetchingMore(true);
    dispatch(fetchChatHistory({ page: page + 1, limit: PAGE_SIZE }))
      .then(() => setPage((prev) => prev + 1))
      .finally(() => setIsFetchingMore(false));
  };

  useEffect(() => {
    const chatList = chatListRef.current;
    if (!chatList) return;

    const handleScroll = () => {
      if (
        chatList.scrollTop + chatList.clientHeight >=
        chatList.scrollHeight - 10
      ) {
        loadMoreChats();
      }
    };

    chatList.addEventListener("scroll", handleScroll);
    return () => chatList.removeEventListener("scroll", handleScroll);
  }, [loadMoreChats]);

  const handleNewChat = async () => {
    try {
      setIsNewChat(true);
      dispatch({ type: "CLEAR_MESSAGES" });
      dispatch(setActiveQuestion("", ""));
      dispatch(setShowWelcomeText(true));
      dispatch(setIsArchived(false));
      dispatch(setActiveTab("chatHistory"));
      navigate("/spend-wise-hub/chat-assistant", {
        replace: true,
      });
    } catch (error) {
      console.error("Error starting a new chat:", error);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#fff",
        borderRadius: "8px",
        padding: "16px 0px",
        width: "280px",
        display: "flex",
        flexDirection: "column",
        height: "-webkit-fill-available",
      }}
    >
      <div
        className="$font-heading-h6-bold"
        style={{ marginLeft: "16px", marginBottom: "16px" }}
      >
        {title}
      </div>

      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #eee",
          marginBottom: "16px",
        }}
      >
        {Object.entries(tabs).map(([key, tab]) => (
          <Tab
            key={key}
            title={tab}
            active={activeTab === key}
            onClick={() => dispatch(setActiveTab(key as ChatHistoryTab))}
          />
        ))}
      </div>

      <div style={{ padding: "0 16px", marginBottom: "16px" }}>
        <Button
          variant="primary"
          style={{
            width: "100%",
            justifyContent: "center",
          }}
          onClick={handleNewChat}
          iconLeading="add"
        >
          New Chat
        </Button>
      </div>
      {activeTab === "chatHistory" && (
        <div style={{ flexGrow: 1 }}>
          <ChatQuestionHistory hideTitle={true} />
        </div>
      )}
      {activeTab === "favourite" && <FavouriteList />}
      {activeTab === "archive" && <ArchieveList />}
    </div>
  );
};
interface TabProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  title: string;
}
export const Tab = (props: TabProps) => {
  const { active, title, ...rest } = props;
  return (
    <div
      {...rest}
      style={{
        flex: 1,
        textAlign: "center",
        padding: "8px 0",
        cursor: "pointer",
        borderBottom: active ? "2px solid #005CBB" : "none",
        fontWeight: active ? "600" : "normal",
        color: active ? "#005CBB" : "#777777",
      }}
    >
      {title}
    </div>
  );
};
export default ChatHistory;
